import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
} from 'react';
import { useBilling } from './BillingContext';
import { useWorkspace } from './WorkspaceContext';
import { serverIsoToMillis } from '../utils/datetime';

/**
 * TrialStatusContext — derives trial-expiration state for the active
 * workspace from the existing billing payload (no extra network call).
 *
 * Plan-TrialExpiration.md §C Phase 2.1.
 *
 *  - `isExpired` is true ONLY when subscription_status === "trialing" AND
 *    trial_ends_at is in the past. Paid plans never trip this gate.
 *  - `daysRemaining` is the ceil-d days from now → trial_ends_at,
 *    surfaced for the pre-expiry banner. `null` when not trialing or
 *    when no `trial_ends_at` is present (shouldn't happen post-Part A
 *    backfill, but legacy workspaces that haven't been touched yet stay
 *    safe). When the trial has already expired (`isExpired === true`),
 *    `daysRemaining` is 0 — banner code keys off both `daysRemaining`
 *    and `subscription_status === "trialing"` so it auto-hides once we
 *    flip to `active` post-checkout.
 *  - `isOwner` mirrors `useWorkspace().isOwner` — surfaced here so the
 *    blocker modal can branch copy without pulling another context.
 *  - `ownerEmail` is derived from `WorkspaceContext.members` (the
 *    member with `is_owner === true`). Falls back to `null` when the
 *    members fetch hasn't landed yet — blocker copy renders an
 *    "an admin" fallback in that case.
 */
export interface TrialStatus {
  isExpired: boolean;
  /**
   * Plan-RemoveFreeTrial.md — true for a brand-new workspace that never got
   * a trial (`subscription_status === "incomplete"`, plan=null). Drives the
   * onboarding SubscriptionRequiredGate. Independent of `isExpired` (which
   * stays scoped to expired *trialing* workspaces) so existing trial users'
   * UI is completely untouched. Internal (`is_company_owned`) workspaces are
   * exempt, matching the trial-gate carve-out.
   */
  requiresPayment: boolean;
  daysRemaining: number | null;
  trialEndsAt: string | null;
  isOwner: boolean;
  ownerEmail: string | null;
  workspaceName: string | null;
}

const TrialStatusContext = createContext<TrialStatus | undefined>(undefined);

const computeDaysRemaining = (iso: string | null | undefined): number | null => {
  const end = serverIsoToMillis(iso);
  if (end === null) return null;
  const ms = end - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

export const TrialStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { billing } = useBilling();
  const { activeWorkspace, members, isOwner } = useWorkspace();

  const value = useMemo<TrialStatus>(() => {
    const trialEndsAt = billing?.trial_ends_at ?? null;
    const isTrialing = billing?.subscription_status === 'trialing';
    // Internal workspaces (is_company_owned: true) bypass Stripe entirely
    // per types/billing.ts:174-183. Trial fields may still be present on
    // legacy/inconsistent docs, but the trial gate must never fire here —
    // there's no upgrade path for the user to take (BillingPage already
    // routes them to the "Managed by admin" modal).
    const isInternal = !!billing?.is_company_owned;

    let isExpired = false;
    if (!isInternal && isTrialing && trialEndsAt) {
      const end = serverIsoToMillis(trialEndsAt);
      if (end !== null) {
        isExpired = end < Date.now();
      }
    }

    const daysRemaining = !isInternal && isTrialing
      ? computeDaysRemaining(trialEndsAt)
      : null;

    // Plan-RemoveFreeTrial.md — a never-trialed, never-paid workspace. The
    // backend locks it at "incomplete" (402 reason:"subscription_required")
    // until the owner picks a plan. Internal workspaces are exempt (same
    // carve-out as the trial gate above).
    const requiresPayment =
      !isInternal && billing?.subscription_status === 'incomplete';

    const ownerMember = members.find((m) => m.is_owner);
    const ownerEmail = ownerMember?.email ?? null;

    return {
      isExpired,
      requiresPayment,
      daysRemaining,
      trialEndsAt,
      isOwner,
      ownerEmail,
      workspaceName: activeWorkspace?.name ?? null,
    };
  }, [billing, members, isOwner, activeWorkspace]);

  return (
    <TrialStatusContext.Provider value={value}>
      {children}
    </TrialStatusContext.Provider>
  );
};

export const useTrialStatus = (): TrialStatus => {
  const ctx = useContext(TrialStatusContext);
  if (!ctx) {
    throw new Error('useTrialStatus must be used within a TrialStatusProvider');
  }
  return ctx;
};
